import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { XMarkIcon, PlusIcon, TrashIcon, SparklesIcon, ArrowPathIcon, MagnifyingGlassIcon, FolderIcon } from '@heroicons/react/24/outline'
import SmartBookmarkHelper from './SmartBookmarkHelper'
import IconSearchModal from './IconSearchModal'
import LocalIconsModal from './LocalIconsModal'

const BookmarkEditModal = ({
  isOpen,
  onClose,
  onSave,
  bookmark = null,
  groupName = '',
  availableGroups = []
}) => {
  const [selectedGroup, setSelectedGroup] = useState(groupName)
  const [bookmarkLinks, setBookmarkLinks] = useState([])
  const [originalBookmarkLinks, setOriginalBookmarkLinks] = useState([]) // 保存原始状态
  const [originalSelectedGroup, setOriginalSelectedGroup] = useState(groupName) // 保存原始分组
  const [showSmartHelper, setShowSmartHelper] = useState(false)
  const [currentLinkIndex, setCurrentLinkIndex] = useState(null)
  
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm()

  useEffect(() => {
    if (isOpen && bookmark) {
      const bookmarkName = Object.keys(bookmark)[0]
      const bookmarkData = bookmark[bookmarkName]

      // 设置基本信息
      setValue('name', bookmarkName)

      // 设置链接列表
      const links = Array.isArray(bookmarkData) ? bookmarkData : []
      setBookmarkLinks([...links]) // 创建副本
      setOriginalBookmarkLinks([...links]) // 保存原始状态

      setSelectedGroup(groupName)
      setOriginalSelectedGroup(groupName) // 保存原始分组
    } else if (isOpen) {
      // 新建书签
      reset()
      const newLinks = [{ abbr: '', href: '', icon: '' }]
      setBookmarkLinks(newLinks)
      setOriginalBookmarkLinks([...newLinks]) // 保存原始状态
      setSelectedGroup(groupName)
      setOriginalSelectedGroup(groupName) // 保存原始分组
    }
  }, [isOpen, bookmark, groupName, setValue, reset])

  const onSubmit = (data) => {
    // 验证所有链接都有必填字段
    const validLinks = []
    const invalidLinks = []

    bookmarkLinks.forEach((link, index) => {
      if (!link.href || !link.href.trim()) {
        invalidLinks.push(`第 ${index + 1} 个链接缺少链接地址`)
      } else if (!isValidUrl(link.href)) {
        invalidLinks.push(`第 ${index + 1} 个链接地址格式无效`)
      } else {
        // 确保URL有协议
        let normalizedUrl = link.href.trim()
        if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
          normalizedUrl = 'http://' + normalizedUrl
        }
        validLinks.push({
          ...link,
          abbr: link.abbr?.trim() || '', // 缩写可以为空
          href: normalizedUrl
        })
      }
    })

    // 如果有无效链接，显示错误信息
    if (invalidLinks.length > 0) {
      alert(`请完善以下信息：\n${invalidLinks.join('\n')}`)
      return
    }

    // 如果没有任何有效链接，提示用户
    if (validLinks.length === 0) {
      alert('请至少添加一个有效的书签链接')
      return
    }

    const bookmarkData = {
      [data.name]: validLinks
    }

    onSave(selectedGroup, bookmarkData)
    onClose()
  }

  const addLink = () => {
    setBookmarkLinks([...bookmarkLinks, { abbr: '', href: '', icon: '' }])
  }

  const updateLink = (index, field, value) => {
    const newLinks = [...bookmarkLinks]
    newLinks[index][field] = value
    setBookmarkLinks(newLinks)
  }

  // 验证URL格式
  const isValidUrl = (url) => {
    if (!url || !url.trim()) return false
    try {
      new URL(url)
      return true
    } catch {
      // 尝试添加协议
      try {
        new URL('http://' + url)
        return true
      } catch {
        return false
      }
    }
  }

  const removeLink = (index) => {
    setBookmarkLinks(bookmarkLinks.filter((_, i) => i !== index))
  }

  // 图标相关状态与工具
  const [showIconSearch, setShowIconSearch] = useState(false)
  const [showLocalIcons, setShowLocalIcons] = useState(false)
  const [isExtractingIcon, setIsExtractingIcon] = useState(false)
  const [availableIcons, setAvailableIcons] = useState([])
  const [showIconSelector, setShowIconSelector] = useState(false)

  const checkIconAvailability = async (iconUrl) => {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => resolve(true)
      img.onerror = () => resolve(false)
      img.src = iconUrl
      setTimeout(() => resolve(false), 3000)
    })
  }

  const normalizeUrl = (url) => {
    if (!url || typeof url !== 'string') return null
    try {
      const trimmedUrl = url.trim()
      if (!trimmedUrl) return null
      if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) return trimmedUrl
      if (trimmedUrl.startsWith('//')) return 'http:' + trimmedUrl
      return 'http://' + trimmedUrl
    } catch (error) {
      console.error('Error in normalizeUrl:', error)
      return null
    }
  }

  const extractIconsFromUrl = async (url) => {
    if (!url) return []
    try {
      setIsExtractingIcon(true)
      const normalizedUrl = normalizeUrl(url)
      if (!normalizedUrl) throw new Error('无效的URL格式')
      const urlObj = new URL(normalizedUrl)
      const baseUrl = `${urlObj.protocol}//${urlObj.host}`
      const serviceName = urlObj.hostname.replace('www.', '').split('.')[0]

      const possibleIcons = [
        { url: `${baseUrl}/favicon.ico`, name: 'Favicon (ICO)', type: 'favicon' },
        { url: `${baseUrl}/favicon.png`, name: 'Favicon (PNG)', type: 'favicon' },
        { url: `${baseUrl}/favicon.svg`, name: 'Favicon (SVG)', type: 'favicon' },
        { url: `${baseUrl}/apple-touch-icon.png`, name: 'Apple Touch Icon', type: 'favicon' },
        { url: `${baseUrl}/apple-touch-icon-precomposed.png`, name: 'Apple Touch Icon (Precomposed)', type: 'favicon' },
        { url: `${baseUrl}/android-chrome-192x192.png`, name: 'Android Chrome Icon (192px)', type: 'favicon' },
        { url: `${baseUrl}/android-chrome-512x512.png`, name: 'Android Chrome Icon (512px)', type: 'favicon' },
        { url: `${baseUrl}/mstile-150x150.png`, name: 'MS Tile Icon', type: 'favicon' },
        { url: `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=16`, name: 'Google Favicon (16px)', type: 'favicon' },
        { url: `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=32`, name: 'Google Favicon (32px)', type: 'favicon' },
        { url: `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=64`, name: 'Google Favicon (64px)', type: 'favicon' },
        { url: `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=128`, name: 'Google Favicon (128px)', type: 'favicon' },
        { url: `https://icons.duckduckgo.com/ip3/${urlObj.hostname}.ico`, name: 'DuckDuckGo Favicon', type: 'favicon' },
        { url: `https://favicons.githubusercontent.com/${urlObj.hostname}`, name: 'GitHub Favicon Service', type: 'favicon' },
        { url: `${serviceName}.png`, name: `Dashboard Icon (${serviceName}.png)`, type: 'dashboard' },
        { url: `${serviceName}.svg`, name: `Dashboard Icon (${serviceName}.svg)`, type: 'dashboard' },
        { url: `si-${serviceName}`, name: `Simple Icon (si-${serviceName})`, type: 'simple' },
        { url: `simple-icons:${serviceName}`, name: `Simple Icons (${serviceName})`, type: 'iconify' },
        { url: `mdi-${serviceName}`, name: `Material Icon (mdi-${serviceName})`, type: 'material' },
        { url: `mdi:${serviceName}`, name: `MDI (${serviceName})`, type: 'iconify' },
        { url: `logos:${serviceName}`, name: `Logo (${serviceName})`, type: 'iconify' },
        { url: `devicon:${serviceName}`, name: `DevIcon (${serviceName})`, type: 'iconify' },
        { url: `skill-icons:${serviceName}`, name: `Skill Icon (${serviceName})`, type: 'iconify' }
      ]

      const foundIcons = []
      for (const icon of possibleIcons.filter(i => i.type === 'favicon')) {
        const ok = await checkIconAvailability(icon.url)
        if (ok) foundIcons.push(icon)
      }
      foundIcons.push(...possibleIcons.filter(i => i.type !== 'favicon'))
      return foundIcons
    } catch (e) {
      console.error('提取图标失败:', e)
      return []
    } finally {
      setIsExtractingIcon(false)
    }
  }

  const handleExtractIconForLink = async (index) => {
    const href = bookmarkLinks[index]?.href
    if (!href) {
      alert('请先输入链接地址\n\n💡 支持格式：\n• 完整URL：https://example.com\n• 简化格式：example.com\n• 子域名：app.example.com')
      return
    }
    setCurrentLinkIndex(index)
    const icons = await extractIconsFromUrl(href)
    setAvailableIcons(icons)
    setShowIconSelector(true)
  }

  const handleSelectIcon = (iconUrl) => {
    if (currentLinkIndex !== null) {
      updateLink(currentLinkIndex, 'icon', iconUrl)
    }
    setShowIconSelector(false)
    setAvailableIcons([])
  }

  const handleSelectSearchIcon = (iconPath) => {
    if (currentLinkIndex !== null) {
      updateLink(currentLinkIndex, 'icon', iconPath)
    }
    setShowIconSearch(false)
  }

  const handleSelectLocalIcon = (iconPath) => {
    if (currentLinkIndex !== null) {
      updateLink(currentLinkIndex, 'icon', iconPath)
    }
    setShowLocalIcons(false)
  }

  const getIconPreviewUrl = (iconValue) => {
    if (!iconValue || typeof iconValue !== 'string') return null
    try {
      if (iconValue.startsWith('http')) return iconValue
      if (iconValue.endsWith('.png') || iconValue.endsWith('.svg') || iconValue.endsWith('.webp')) {
        return `https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/png/${iconValue}`
      }
      if (iconValue.startsWith('si-')) {
        const iconName = iconValue.replace('si-', '')
        return `https://cdn.jsdelivr.net/npm/simple-icons@v9/${iconName}.svg`
      }
      if (iconValue.startsWith('mdi-')) {
        const iconName = iconValue.replace('mdi-', '')
        return `https://cdn.jsdelivr.net/npm/@mdi/svg@7.2.96/svg/${iconName}.svg`
      }
      return null
    } catch (error) {
      console.error('Error in getIconPreviewUrl:', error)
      return null
    }
  }
  // 处理智能助手填充数据
  const handleSmartFill = (data) => {
    if (currentLinkIndex !== null) {
      // 更新指定索引的链接
      const newLinks = [...bookmarkLinks]
      newLinks[currentLinkIndex] = {
        abbr: data.abbr || '',
        href: data.href || '',
        icon: data.icon || ''
      }
      setBookmarkLinks(newLinks)
    } else {
      // 添加新链接
      setBookmarkLinks([...bookmarkLinks, {
        abbr: data.abbr || '',
        href: data.href || '',
        icon: data.icon || ''
      }])
    }
    setShowSmartHelper(false)
    setCurrentLinkIndex(null)
  }

  // 显示智能助手
  const showSmartHelperForLink = (index = null) => {
    setCurrentLinkIndex(index)
    setShowSmartHelper(true)
  }

  // 处理取消操作 - 恢复到原始状态
  const handleCancel = () => {
    // 恢复原始数据
    setBookmarkLinks([...originalBookmarkLinks])
    setSelectedGroup(originalSelectedGroup)

    // 重置表单
    if (bookmark) {
      const bookmarkName = Object.keys(bookmark)[0]
      setValue('name', bookmarkName)
    } else {
      reset()
    }

    // 关闭模态框
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" onClick={handleCancel} />
        
        <div className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-gray-800 shadow-xl rounded-2xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">
              {bookmark ? '编辑书签' : '添加书签'}
            </h3>
            <button
              onClick={handleCancel}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* 基本信息 */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-200">基本信息</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    书签名称 *
                  </label>
                  <input
                    {...register('name', { required: '书签名称是必填的' })}
                    className="input"
                    placeholder="例如：开发工具"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    书签分组
                  </label>
                  <select
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    className="select"
                  >
                    {availableGroups.map(group => (
                      <option key={group} value={group}>{group}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* 书签链接 */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-200">书签链接</h4>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => showSmartHelperForLink()}
                    className="btn-secondary btn-sm"
                  >
                    <SparklesIcon className="w-4 h-4 mr-1" />
                    智能添加
                  </button>
                  <button
                    type="button"
                    onClick={addLink}
                    className="btn-secondary btn-sm"
                  >
                    <PlusIcon className="w-4 h-4 mr-1" />
                    手动添加
                  </button>
                </div>
              </div>
              
              {bookmarkLinks.map((link, index) => (
                <div key={index} className="space-y-4 p-4 bg-gray-700/50 rounded-lg">
                  {/* 第一行：缩写和链接地址 */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        缩写
                      </label>
                      <input
                        value={link.abbr}
                        onChange={(e) => updateLink(index, 'abbr', e.target.value)}
                        className="input"
                        placeholder="GH (可选)"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        链接地址 <span className="text-red-400">*</span>
                      </label>
                      <input
                        value={link.href}
                        onChange={(e) => updateLink(index, 'href', e.target.value)}
                        className={`input ${!link.href?.trim() || (link.href?.trim() && !isValidUrl(link.href)) ? 'border-red-500 focus:border-red-500' : ''}`}
                        placeholder="github.com/ 或 github.com"
                        type="url"
                        required
                        onFocus={(e) => {
                          // 如果输入框为空，自动填入 http://
                          if (!e.target.value) {
                            updateLink(index, 'href', 'http://')
                          }
                        }}
                      />
                      {!link.href?.trim() ? (
                        <p className="mt-1 text-sm text-red-400">链接地址是必填的</p>
                      ) : link.href?.trim() && !isValidUrl(link.href) ? (
                        <p className="mt-1 text-sm text-red-400">请输入有效的URL地址</p>
                      ) : null}
                    </div>
                  </div>

                  {/* 第二行：图标输入框和按钮 */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-300">
                      图标
                    </label>
                    <div className="flex gap-2 items-center">
                      {/* 带图标预览的输入框 - 调整宽度 */}
                      <div className="flex-1 max-w-md flex items-center bg-gray-800 border border-gray-600 rounded-lg focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 overflow-hidden">
                        <input
                          value={link.icon || ''}
                          onChange={(e) => updateLink(index, 'icon', e.target.value)}
                          className="flex-1 bg-transparent px-3 py-2 text-white placeholder-gray-400 focus:outline-none min-w-0"
                          placeholder="plex.png 或 si-plex 或 https://..."
                        />
                        {/* 图标预览 */}
                        <div className="w-8 h-8 mx-2 flex items-center justify-center flex-shrink-0">
                          {link.icon && getIconPreviewUrl(link.icon) ? (
                            <img
                              src={getIconPreviewUrl(link.icon)}
                              alt="图标预览"
                              className="w-6 h-6 object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none'
                                e.target.nextSibling.style.display = 'block'
                              }}
                            />
                          ) : null}
                          <span
                            className="text-xs text-gray-500"
                            style={{ display: (link.icon && getIconPreviewUrl(link.icon)) ? 'none' : 'block' }}
                          >
                            {link.icon ? '?' : '📷'}
                          </span>
                        </div>
                      </div>

                      {/* 按钮组 */}
                      <button
                        type="button"
                        onClick={() => handleExtractIconForLink(index)}
                        disabled={isExtractingIcon || !link.href}
                        className="btn-secondary px-3 py-2 min-w-[44px] flex items-center justify-center flex-shrink-0"
                        title="从链接地址自动提取图标"
                      >
                        <ArrowPathIcon className={`h-4 w-4 ${isExtractingIcon ? 'animate-spin' : ''}`} />
                      </button>
                      <button
                        type="button"
                        onClick={() => { setCurrentLinkIndex(index); setShowIconSearch(true) }}
                        className="btn-secondary px-3 py-2 min-w-[44px] flex items-center justify-center flex-shrink-0"
                        title="搜索在线图标"
                      >
                        <MagnifyingGlassIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => { setCurrentLinkIndex(index); setShowLocalIcons(true) }}
                        className="btn-secondary px-3 py-2 min-w-[44px] flex items-center justify-center flex-shrink-0"
                        title="本地图标库"
                      >
                        <FolderIcon className="h-4 w-4" />
                      </button>

                      {/* 操作按钮 */}
                      <div className="flex items-center space-x-2 ml-auto">
                        <button
                          type="button"
                          onClick={() => showSmartHelperForLink(index)}
                          className="p-2 text-blue-400 hover:text-blue-300 hover:bg-gray-600 rounded-lg transition-colors"
                          title="智能填充"
                        >
                          <SparklesIcon className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeLink(index)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-gray-600 rounded-lg transition-colors"
                          title="删除链接"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* 说明文字 */}
                    <p className="text-xs text-gray-400">
                      支持 Dashboard Icons、Simple Icons、Material Icons、完整URL 或本地图标
                    </p>
                  </div>
                </div>
              ))}

              {bookmarkLinks.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <p>暂无书签链接</p>
                  <div className="flex justify-center space-x-2 mt-2">
                    <button
                      type="button"
                      onClick={() => showSmartHelperForLink()}
                      className="btn-secondary"
                    >
                      <SparklesIcon className="w-4 h-4 mr-1" />
                      智能添加
                    </button>
                    <button
                      type="button"
                      onClick={addLink}
                      className="btn-secondary"
                    >
                      <PlusIcon className="w-4 h-4 mr-1" />
                      手动添加
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 操作按钮 */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-700">
              <button
                type="button"
                onClick={handleCancel}
                className="btn-secondary"
              >
                取消
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                {bookmark ? '更新书签' : '添加书签'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* 智能助手模态框 */}
      {showSmartHelper && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" onClick={() => setShowSmartHelper(false)} />

            <div className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-gray-800 shadow-xl rounded-2xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <SparklesIcon className="w-5 h-5 mr-2 text-blue-400" />
                  智能书签助手
                </h3>
                <button
                  onClick={() => setShowSmartHelper(false)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <SmartBookmarkHelper
                onFillData={handleSmartFill}
                className="mb-4"
              />

              <div className="flex justify-end pt-4 border-t border-gray-700">
                <button
                  onClick={() => setShowSmartHelper(false)}
                  className="btn-secondary"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* 图标选择器模态框 */}
      {showIconSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">选择图标</h3>
              <button onClick={() => setShowIconSelector(false)} className="text-gray-400 hover:text-white">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {availableIcons.map((icon, i) => (
                <div key={i} onClick={() => handleSelectIcon(icon.url)} className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors">
                  <div className="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    {icon.type === 'favicon' ? (
                      <img src={icon.url} alt={icon.name} className="w-6 h-6 object-contain" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block' }} />
                    ) : (
                      getIconPreviewUrl(icon.url) ? (
                        <img src={getIconPreviewUrl(icon.url)} alt={icon.name} className="w-6 h-6 object-contain" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block' }} />
                      ) : null
                    )}
                    <span className="text-xs text-gray-400 hidden">?</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text白 truncate">{icon.name}</p>
                    <p className="text-xs text-gray-400 truncate">{icon.url}</p>
                  </div>
                </div>
              ))}
            </div>
            {availableIcons.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-400">未找到可用的图标</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 图标搜索模态框 */}
      <IconSearchModal isOpen={showIconSearch} onClose={() => setShowIconSearch(false)} onSelectIcon={handleSelectSearchIcon} />
      {/* 本地图标库模态框 */}
      <LocalIconsModal isOpen={showLocalIcons} onClose={() => setShowLocalIcons(false)} onSelectIcon={handleSelectLocalIcon} />
    </div>
  )
}

export default BookmarkEditModal
